import type {
  Wallet,
  WalletDetail,
  WalletNetworkOption,
  WalletAssetOption,
  WalletProviderOption,
  WalletCreationEvent,
  Approval,
  ApprovalDetail,
  AppUser,
  DepEntry,
  DepDeployment,
  DepPreflightCheck,
  DepService,
  DepAcceptanceCheck,
  DepHistoryRow,
  DepOperateReceipt,
  DeploymentStepId,
  DeploymentState,
  Notification,
  Transfer,
  TransferDetail,
  TransferWalletOption,
  TransferAssetOption,
  OrgAdminInvite,
  ReadinessCheckItem,
  OrgThemeConfig,
  RoleDefinition,
  UserGroup,
  UserDetailExtra,
  ServiceAccount,
  AuditEvent,
  SsoConfig,
  MfaMethodPolicy,
  SessionPolicy,
  AuthConfigAuditEntry,
  AuthDiagnosticCheck,
  AuthDiagnosticLogEvent,
  SvcCatalogEntry,
  SvcConnectivityCheck,
  SvcProbeDefaults,
  SvcConfigNode,
  SvcSupportBundleInclude,
  SvcSupportBundleRecord,
  RecoveryCustodian,
  RecoveryHistoryRow,
  RecoveryShard,
  BackupChecklistStep,
  RecoveryRequestFixture,
  CustodianApproval,
  RecoveryExecutionState,
  RecoveryAuditPacket,
  DevApiActivityRow,
  ApiKey,
  WebhookEndpoint,
  WebhookDelivery,
  NotificationPrefGroup,
} from "@/types";
import { DEMO_CUSTOMER } from "@/lib/demo-customer";

/* ── Wallets (8) ─────────────────────────────────────────────── */
export const MOCK_WALLETS: Wallet[] = [
  { id:"WLT-001", name:"HT Operations Hot",    chain:"Ethereum Mainnet", provider:"Fireblocks",       type:"hot",  balanceUsd:12450320, lastSyncAgo:"2m",  status:"active" },
  { id:"WLT-002", name:"HT Treasury Cold",     chain:"Ethereum Mainnet", provider:"Mesh (VaultCore)", type:"cold", balanceUsd:24800000, lastSyncAgo:"5m",  status:"active" },
  { id:"WLT-003", name:"HT BTC Reserve",       chain:"Bitcoin Mainnet",  provider:"BitGo",            type:"cold", balanceUsd:8250000,  lastSyncAgo:"8m",  status:"active" },
  { id:"WLT-004", name:"HT Polygon Ops",       chain:"Polygon Mainnet",  provider:"Fireblocks",       type:"hot",  balanceUsd:1820500,  lastSyncAgo:"1m",  status:"active" },
  { id:"WLT-005", name:"HT Settlement",        chain:"Ethereum Mainnet", provider:"Fireblocks",       type:"warm", balanceUsd:850000,   lastSyncAgo:"3m",  status:"active" },
  { id:"WLT-006", name:"HT Frozen Compliance", chain:"Ethereum Mainnet", provider:"Fireblocks",       type:"hot",  balanceUsd:320000,   lastSyncAgo:"4h",  status:"frozen" },
  { id:"WLT-007", name:"HT Solana Ops",        chain:"Solana Mainnet",   provider:"Mesh (VaultCore)", type:"hot",  balanceUsd:null,     lastSyncAgo:null,  status:"pending" },
  { id:"WLT-008", name:"HT Sandbox Test",      chain:"Ethereum Sepolia", provider:"Fireblocks",       type:"hot",  balanceUsd:0,        lastSyncAgo:"1m",  status:"sandbox" },
];

/* ── Wallet Detail (WAL-01 · Figma 32245:4117) ───────────────── */
export const MOCK_WALLET_DETAILS: WalletDetail[] = [
  {
    id: "WLT-001",
    name: "HT Operations Hot",
    stats: [
      { value: "$12.45M", label: "Total Balance", hint: "+1.3% 24h", positiveHint: true },
      { value: "3", label: "Assets" },
      { value: "142", label: "Transfers (30d)", hint: "+18 vs prior", positiveHint: true },
      { value: "● Fresh", label: "Source Freshness", hint: "2m ago", tone: "success" },
    ],
    balances: [
      { symbol: "ETH", name: "Ether", amount: "4,125.50 ETH", usd: "$10,313,750", iconLabel: "ETH" },
      { symbol: "USDC", name: "USD Coin", amount: "1,850,000.00 USDC", usd: "$1,850,000", iconLabel: "USD" },
      { symbol: "USDT", name: "Tether USD", amount: "286,570.00 USDT", usd: "$286,570", iconLabel: "USD" },
    ],
    routes: [
      { title: "Send", supported: true, detail: "ETH, USDC, USDT · Max single: $5M" },
      { title: "Receive", supported: true, detail: "All assets · No limit" },
      { title: "Sweep to Cold", supported: true, detail: "Auto-sweep above $15M threshold" },
      { title: "Staking", supported: false, detail: "Hot wallets excluded by policy" },
    ],
    recentTransfers: [
      { id: "TXI-001", direction: "sent", asset: "ETH", amount: "50.00", status: "Reconciled", statusVariant: "success", time: "10:30 UTC" },
      { id: "TXI-002", direction: "received", asset: "USDC", amount: "250,000.00", status: "Reconciled", statusVariant: "success", time: "10:15 UTC" },
      { id: "TXI-003", direction: "sent", asset: "ETH", amount: "100.00", status: "Pending Approval", statusVariant: "warning", time: "10:42 UTC" },
    ],
    properties: [
      { k: "Wallet ID", v: "WLT-001" },
      { k: "Type", v: "Hot" },
      { k: "Chain", v: "Ethereum Mainnet" },
      { k: "Provider", v: "Fireblocks" },
      { k: "Created", v: "2026-03-12" },
      { k: "Admin", v: "Priya Sharma (USR-003)" },
      { k: "Policy", v: "POL-001" },
    ],
    evidence: [
      { id: "EVD-001", label: "EVD-001 — Balance attestation" },
      { id: "EVD-002", label: "EVD-002 — Policy compliance" },
    ],
    hasRecovery: true,
  },
];

/* ── Wallet Request fixtures (WAL-05..07) ─────────────────────── */
export const WALLET_NETWORKS: WalletNetworkOption[] = [
  { id: "Sandbox Ledger", label: "Sandbox", sub: "Public Sandbox · Development", glyph: "⛓", chain: "sandbox-ledger", apiNetwork: "public-sandbox" },
  { id: "Ethereum Mainnet", label: "Ethereum", sub: "EIP-155 chain 1 · EVM", glyph: "Ξ" },
  { id: "Bitcoin Mainnet", label: "Bitcoin", sub: "Mainnet · UTXO", glyph: "₿" },
  { id: "Solana Mainnet", label: "Solana", sub: "Mainnet · SVM", glyph: "◎" },
];

export const WALLET_ASSETS: WalletAssetOption[] = [
  { symbol: "SBX", name: "Sandbox Token", network: "Sandbox Ledger" },
  { symbol: "ETH", name: "Ether", network: "Ethereum Mainnet" },
  { symbol: "USDC", name: "USD Coin", network: "Ethereum Mainnet" },
  { symbol: "BTC", name: "Bitcoin", network: "Bitcoin Mainnet" },
  { symbol: "SOL", name: "Solana", network: "Solana Mainnet" },
];

export const WALLET_PROVIDERS: WalletProviderOption[] = [
  { id: "Fireblocks", name: "Fireblocks", networks: ["Ethereum Mainnet", "Bitcoin Mainnet", "Solana Mainnet"], model: "Custody + MPC signing" },
  { id: "Anchorage", name: "Anchorage", networks: ["Ethereum Mainnet"], model: "Qualified custodian" },
  { id: "Ledger Vault", name: "Ledger Vault", networks: ["Bitcoin Mainnet"], model: "HSM-backed custody" },
  { id: "Coinbase Prime", name: "Coinbase Prime", networks: ["Solana Mainnet"], model: "Prime brokerage custody" },
  { id: "Mesh (VaultCore)", name: "Alloy Mesh", networks: ["Sandbox Ledger", "Solana Mainnet"], model: "Mesh routing · VaultCore custody", profileId: "sandbox-no-signing" },
];

export const WALLET_GROUPS = ["Operations", "Treasury", "Payments", "Reserve", "Settlement"];

export const WALLET_CREATION_EVENTS: WalletCreationEvent[] = [
  { title: "Configuration Submitted", meta: "2026-09-15 10:30 UTC — by Priya Sharma (USR-003)", detail: "Label: HT Solana Ops · Type: Hot · Chain: Solana Mainnet · Provider: Mesh (VaultCore)", state: "done" },
  { title: "Policy Evaluation Passed", meta: "2026-09-15 10:30 UTC — POL-003 (Wallet Creation Policy)", detail: "Requires admin approval for new chain. Approval route: Marcus Webb or Hannah Lindqvist.", state: "done" },
  { title: "Pending Approval", meta: "Waiting since 10:31 UTC (14 min)", detail: "Assigned to: Marcus Webb (USR-005) — SLA: 4h remaining", state: "active" },
  { title: "Provider Provisioning", meta: "Pending approval", state: "pending" },
];

/* ── Approvals ──────────────────────────────────────────────── */
export const MOCK_APPROVALS: Approval[] = [
  { id:"TXI-003", type:"transfer", title:"Transfer: 100.00 ETH → HT Treasury Cold",       meta:"TXI-003 · Sai Suman Kanuganti · 10:42 UTC",    hint:"0 of 1 required · You are the assigned approver", slaLeft:"3h 58m left", slaSeverity:"ok",   approvalsRequired:1, approvalsDone:0, progressPercent:null, status:"pending", urgent:false },
  { id:"WLT-007", type:"wallet",   title:"Wallet Creation: HT Solana Ops",                meta:"WLT-007 · Priya Sharma · 10:31 UTC",     hint:"0 of 1 required · New chain activation",          slaLeft:"3h 46m left", slaSeverity:"ok",   approvalsRequired:1, approvalsDone:0, progressPercent:null, status:"pending", urgent:false },
  { id:"POL-001", type:"policy",   title:"Policy Change: Increase daily transfer limit to $10M", meta:"POL-001 update · Hannah Lindqvist · 09:15 UTC", hint:"1 of 2 required · Elena Vasquez approved · Waiting for you", slaLeft:"42m left", slaSeverity:"warn", approvalsRequired:2, approvalsDone:1, progressPercent:50,   status:"pending", urgent:true },
  { id:"TXI-004", type:"transfer", title:"Transfer: 250,000.00 USDC → Partner Vault",     meta:"TXI-004 · Elena Vasquez · 08:52 UTC",     hint:"0 of 2 required · Awaiting first approval",       slaLeft:"6h 12m left", slaSeverity:"ok",   approvalsRequired:2, approvalsDone:0, progressPercent:null, status:"pending", urgent:false },
  { id:"TXI-005", type:"transfer", title:"Transfer: 2.1 BTC → Cold Storage",              meta:"TXI-005 · Amina Diallo · 08:10 UTC",      hint:"0 of 1 required · Assigned to compliance",        slaLeft:"7h 44m left", slaSeverity:"ok",   approvalsRequired:1, approvalsDone:0, progressPercent:null, status:"pending", urgent:false },
];

export const MOCK_COMPLETED_APPROVALS: Approval[] = [
  { id:"TXI-001", type:"transfer", title:"Transfer: 50.00 ETH → HT Treasury Cold",                   meta:"Approved by Sai Suman Kanuganti · 2/2 · Yesterday 14:22 UTC", hint:"", slaLeft:"", slaSeverity:"ok", approvalsRequired:2, approvalsDone:2, progressPercent:null, status:"approved", urgent:false },
  { id:"TXI-002", type:"transfer", title:"Transfer: 1,000,000.00 USDC — velocity limit exceeded",   meta:"Rejected by Elena Vasquez · Yesterday 11:05 UTC",         hint:"", slaLeft:"", slaSeverity:"ok", approvalsRequired:2, approvalsDone:0, progressPercent:null, status:"rejected", urgent:false },
  { id:"WLT-003", type:"wallet",   title:"Wallet Creation: HT BTC Reserve",                          meta:"Approved by Sai Suman Kanuganti · 1/1 · Sep 14 09:33 UTC",     hint:"", slaLeft:"", slaSeverity:"ok", approvalsRequired:1, approvalsDone:1, progressPercent:null, status:"approved", urgent:false },
];

/* ── Approval detail records (APR-01..05 data) ─────────────── */
export const MOCK_APPROVAL_DETAILS: ApprovalDetail[] = [
  {
    id: "TXI-003",
    approvalId: "APR-001",
    transferId: "TXI-003",
    amount: "100.00 ETH ≈ $250,000",
    source: "HT Operations Hot (WLT-001)",
    destination: "HT Treasury Cold (WLT-002) (internal)",
    requester: "Sai Suman Kanuganti (USR-002) · Treasury Operator",
    memo: "Sweep to cold — weekly rebalance",
    risk: "low",
    policyTitle: "POL-001 — Transfer Policy",
    policyDesc: "Internal transfer. Amount within $5M daily limit. Today's volume: $375K / $5M. Single-transfer limit: OK ($250K < $5M).",
    screeningTitle: "No screening required",
    screeningDesc: "Internal wallet-to-wallet transfer. Destination is a governed wallet under the same organization.",
    quorumLabel: "Required: 1 of 2 · SLA: 3h 58m remaining",
    quorumPct: 50,
    approvers: [
      { initials: "MW", name: "Marcus Webb (You)", role: "Security Admin · Assigned", status: "action", you: true },
      { initials: "EV", name: "Elena Vasquez", role: "Compliance Officer · Alternate", status: "available" },
    ],
  },
  {
    id: "TXI-004",
    approvalId: "APR-002",
    transferId: "TXI-004",
    amount: "250,000.00 USDC ≈ $250,000",
    source: "HT Operations Hot (WLT-001)",
    destination: "Partner Vault (external)",
    requester: "Elena Vasquez (USR-004) · Compliance Officer",
    memo: "Partner vault top-up — settlement batch",
    risk: "high",
    policyTitle: "POL-001 — Transfer Policy",
    policyDesc: "External transfer. Within $5M daily limit. First-time external counterparty above $10K requires dual approval.",
    screeningTitle: "External recipient screening required",
    screeningDesc: "Destination is an external counterparty — reviewed against sanctioned list and counterparty registry.",
    quorumLabel: "Required: 2 of 2 · SLA: 6h 12m remaining",
    quorumPct: 0,
    approvers: [
      { initials: "EV", name: "Elena Vasquez", role: "Compliance Officer · Assigned", status: "action", you: true },
      { initials: "MW", name: "Marcus Webb", role: "Security Admin · Alternate", status: "available" },
    ],
  },
  {
    id: "TXI-005",
    approvalId: "APR-003",
    transferId: "TXI-005",
    amount: "2.1 BTC ≈ $98,500",
    source: "HT BTC Reserve (WLT-003)",
    destination: "HT Treasury Cold (WLT-002) (internal)",
    requester: "Amina Diallo (USR-006) · Finance / Recon Lead",
    memo: "Cold storage consolidation batch #1",
    risk: "low",
    policyTitle: "POL-001 — Transfer Policy",
    policyDesc: "Internal transfer. Amount within $5M daily limit. Single-transfer limit: OK ($98.5K < $5M).",
    screeningTitle: "No screening required",
    screeningDesc: "Internal wallet-to-wallet transfer. Destination is a governed wallet under the same organization.",
    quorumLabel: "Required: 1 of 1 · SLA: 7h 44m remaining",
    quorumPct: 0,
    approvers: [
      { initials: "AD", name: "Amina Diallo (You)", role: "Finance / Recon Lead · Assigned", status: "action", you: true },
    ],
  },
];

/* ── Users (11) ─────────────────────────────────────────────── */
export const MOCK_USERS: AppUser[] = [
  { id:"USR-001", name:"Maya Chen",           email:`maya.chen@${DEMO_CUSTOMER.emailDomain}`,           role:"Executive / Board Viewer", groups:["Executives"],             mfa:"Passkey",                status:"Active",   lastActive:"10:32 UTC" },
  { id:"USR-002", name:"Sai Suman Kanuganti", email:`daniel.okafor@${DEMO_CUSTOMER.emailDomain}`,       role:"Treasury Operator",        groups:["Treasury Operators"],     mfa:"TOTP + Passkey",          status:"Active",   lastActive:"10:44 UTC" },
  { id:"USR-003", name:"Priya Sharma",        email:`priya.sharma@${DEMO_CUSTOMER.emailDomain}`,        role:"Wallet Administrator",     groups:["Treasury Operators"],     mfa:"TOTP",                   status:"Active",   lastActive:"09:18 UTC" },
  { id:"USR-004", name:"Elena Vasquez",       email:`elena.vasquez@${DEMO_CUSTOMER.emailDomain}`,       role:"Compliance Officer",       groups:["Compliance Team"],        mfa:"Passkey",                status:"Active",   lastActive:"10:40 UTC" },
  { id:"USR-005", name:"Marcus Webb",         email:`marcus.webb@${DEMO_CUSTOMER.emailDomain}`,         role:"Security Admin",           groups:[],                        mfa:"Hardware Key + TOTP",    status:"Active",   lastActive:"10:45 UTC" },
  { id:"USR-006", name:"Amina Al-Rashid",     email:`amina.alrashid@${DEMO_CUSTOMER.emailDomain}`,      role:"Finance / Recon Lead",     groups:[],                        mfa:"TOTP",                   status:"Active",   lastActive:"08:50 UTC" },
  { id:"USR-007", name:"Hannah Lindqvist",    email:`hannah.lindqvist@${DEMO_CUSTOMER.emailDomain}`,    role:"Organization Admin",       groups:["Engineering"],            mfa:"TOTP + Passkey",          status:"Active",   lastActive:"2026-09-14" },
  { id:"USR-008", name:"Leo Nakamura",        email:`leo.nakamura@${DEMO_CUSTOMER.emailDomain}`,        role:"Developer",                groups:["Engineering"],            mfa:"TOTP",                   status:"Active",   lastActive:"10:42 UTC" },
  { id:"USR-009", name:"Sofia Torres",        email:`sofia.torres@${DEMO_CUSTOMER.emailDomain}`,        role:"Auditor (Read-Only)",      groups:["Compliance Team"],        mfa:"Passkey",                status:"Active",   lastActive:"2026-09-12" },
  { id:"USR-010", name:"James Morton",        email:`james.morton@${DEMO_CUSTOMER.emailDomain}`,        role:"Operator",                 groups:[],                        mfa:"",                       status:"Invited",  lastActive:"—" },
  { id:"USR-011", name:"Rachel Kim",          email:`rachel.kim@${DEMO_CUSTOMER.emailDomain}`,          role:"Support Read-Only",        groups:[],                        mfa:"TOTP",                   status:"Suspended",lastActive:"2026-08-28" },
];

export const USER_ROLES_FILTER = [
  "Executive / Board Viewer","Treasury Operator","Wallet Administrator","Compliance Officer",
  "Security Admin","Finance / Recon Lead","Organization Admin","Developer","Auditor (Read-Only)",
  "Operator","Support Read-Only",
];
export const USER_GROUPS_FILTER = ["Executives","Treasury Operators","Compliance Team","Engineering"];
export const USER_MFA_FILTER = ["Passkey","TOTP","TOTP + Passkey","Hardware Key + TOTP"];
export const USER_STATUS_FILTER = ["Active","Invited","Suspended"];

/* ── Deployment ─────────────────────────────────────────────── */
export const DEP_ENTRIES: DepEntry[] = [
  { id:"sandbox",  type:"sandbox",   title:"Shared Sandbox",       description:"Multi-tenant sandbox for integration testing and development. Pre-provisioned with sample data.", tags:[{ label:"No infrastructure needed", variant:"neutral" },{ label:"Available immediately", variant:"info" }] },
  { id:"dedicated",type:"dedicated", title:"Dedicated Deployment",  description:"Single-tenant production deployment for Harbor Trust Bank, N.A. Requires preflight checklist completion.", tags:[{ label:"Full isolation", variant:"success" },{ label:"Infrastructure required", variant:"warning" }] },
  { id:"existing", type:"existing",  title:"Existing Deployment",   description:"Manage or upgrade an existing deployment. View health, trigger rollbacks, or start upgrade.", tags:[] },
];

export const DEP_TABLE: DepDeployment[] = [
  { id:"DEP-001", customer:"Harbor Trust Bank, N.A.", state:"Preflight", badgeVariant:"info",    lastUpdated:"2026-09-15 10:45 UTC" },
  { id:"DEP-000", customer:"Meridian Capital",       state:"Operating", badgeVariant:"success", lastUpdated:"2026-09-10 14:22 UTC" },
];

export const DEP_PREFLIGHT_CHECKS: DepPreflightCheck[] = [
  { id:"oidc", name:"OIDC / SAML Provider",    status:"passed",  lastChecked:"10:42 UTC", owner:"Identity Team" },
  { id:"tls",  name:"TLS / mTLS Certificates", status:"passed",  lastChecked:"10:40 UTC", owner:"Infra Team" },
  { id:"db",   name:"Service Database",        status:"passed",  lastChecked:"10:38 UTC", owner:"DBA Team" },
  { id:"hsm",  name:"HSM / KMS",               status:"failed",  lastChecked:"10:44 UTC", owner:"Security Team", retryable:true },
  { id:"rpc",  name:"RPC Connectivity",        status:"passed",  lastChecked:"10:41 UTC", owner:"Network Team" },
  { id:"obs",  name:"Observability Stack",     status:"pending", lastChecked:"10:35 UTC", owner:"SRE Team" },
  { id:"rel",  name:"Release Bundle",          status:"passed",  lastChecked:"10:43 UTC", owner:"Release Eng" },
];

export const DEP_STEPS: { id:DeploymentStepId; label:string; notice:string }[] = [
  { id:"preflight",   label:"Preflight",   notice:"DEP-01 · Preflight Validation" },
  { id:"bootstrap",   label:"Bootstrap",    notice:"DEP-02 · Service Bootstrap" },
  { id:"acceptance",  label:"Acceptance",   notice:"DEP-03 · Acceptance Gate" },
  { id:"operate",     label:"Operate",      notice:"DEP-04 · Operate Steady-State" },
  { id:"history",     label:"History",      notice:"DEP-05 · Deployment History" },
];

export const DEP_STATE_FLOW: DeploymentState[] = [
  "draft","preflight-running","preflight-passed","bootstrapping",
  "acceptance-running","accepted","operating",
];

export const DEP_SERVICES: DepService[] = [
  { id:"walletkit",  name:"WalletKit",          status:"running",  percent:100 },
  { id:"vaultcore",  name:"VaultCore",          status:"starting", percent:60 },
  { id:"alloymesh",  name:"Alloy Mesh",         status:"starting", percent:45 },
  { id:"broadcast",  name:"Broadcast Gateway",  status:"running",  percent:100 },
  { id:"policykit",  name:"PolicyKit",          status:"running",  percent:100 },
  { id:"riskguard",  name:"RiskGuard",          status:"running",  percent:100 },
  { id:"reconflow",  name:"ReconFlow",          status:"degraded", percent:80 },
];

export const DEP_ACCEPTANCE_CHECKS: DepAcceptanceCheck[] = [
  { id:"signing",     name:"WalletKit → VaultCore signing path",   detail:"Test signing request completed in 142ms" },
  { id:"provision",   name:"Mesh wallet provisioning path",        detail:"Test wallet created and deleted in 280ms" },
  { id:"broadcast",   name:"Broadcast gateway → chain RPC",        detail:"Test broadcast path to Ethereum Mainnet verified" },
  { id:"policy",      name:"PolicyKit rule evaluation",            detail:"3 test evaluations passed — 0 false positives" },
];

export const DEP_HISTORY: DepHistoryRow[] = [
  { deploymentId:"DEP-2026-0001", version:"v1.8.2-2026-09-10", status:"active",     acceptedBy:"Maya Okonkwo", date:"Sep 12, 2026" },
  { deploymentId:"DEP-2026-0000", version:"v1.7.0-2026-08-01", status:"superseded", acceptedBy:"Maya Okonkwo", date:"Aug 1, 2026" },
];

export const DEP_OPERATE: DepOperateReceipt = {
  deploymentId:"DEP-2026-0001",
  version:"v1.8.2-2026-09-10.sha.a3f8c2d",
  modules:"WalletKit · VaultCore · Mesh · PolicyKit · RiskGuard · Broadcast Gateway · ReconFlow",
  environment:"Production",
  tenant:"harbor-trust",
  acceptedBy:"Maya Okonkwo (Org Admin)",
  acceptedAt:"2026-09-12T09:00:00Z",
};

/* ── Notifications ──────────────────────────────────────────── */
export const MOCK_NOTIFICATIONS: Notification[] = [
  { id:"n1", title:"Approval needed",          message:"TXI-003 · 100 ETH transfer requires your approval", timeAgo:"2m ago",  read:false },
  { id:"n2", title:"Deployment preflight",      message:"DEP-001 preflight 5/7 checks passed",               timeAgo:"15m ago", read:false },
  { id:"n3", title:"Wallet synced",            message:"HT Operations Hot · $12.4M synced 2m ago",          timeAgo:"2m ago",  read:true },
];

/* ── Transfers (TRF-00 list · 7 intents) ─────────────────────── */
export const TRANSFER_STATS = [
  { value:"142",  label:"Total (30d)",      hint:"+18 vs prior", positiveHint:true },
  { value:"3",    label:"Pending Approval", hint:null,           positiveHint:false },
  { value:"1",    label:"Stuck / Failed",   hint:null,           positiveHint:false },
  { value:"$2.4M",label:"Volume Today",     hint:null,           positiveHint:false },
];

export const MOCK_TRANSFERS: Transfer[] = [
  { id:"TXI-001", direction:"send",    route:"HT Ops Hot → 0x8f2e…3a4b", asset:"ETH",   amount:"50.00",   status:"reconciled",       statusLabel:"Reconciled",       statusVariant:"success", created:"10:30", creator:"Sai Suman Kanuganti", wallet:"WLT-001", state:"reconciled" },
  { id:"TXI-002", direction:"receive", route:"0x4a1b…9c2d → HT Ops Hot", asset:"USDC",  amount:"250,000",  status:"reconciled",       statusLabel:"Reconciled",       statusVariant:"success", created:"10:15", creator:"External",      wallet:"WLT-001", state:"reconciled" },
  { id:"TXI-003", direction:"send",    route:"HT Ops Hot → HT Cold",     asset:"ETH",   amount:"100.00",   status:"pending-approval", statusLabel:"Pending Approval", statusVariant:"warning", created:"10:42", creator:"Sai Suman Kanuganti", wallet:"WLT-001", state:"pending-approval" },
  { id:"TXI-004", direction:"send",    route:"HT Ops Hot → 0xc3d…7e2f",  asset:"USDT",  amount:"75,000",   status:"pending-screening",statusLabel:"Pending Screening",statusVariant:"warning", created:"10:40", creator:"treasury-bot",  wallet:"WLT-001", state:"pending-policy" },
  { id:"TXI-005", direction:"send",    route:"HT BTC → 3FE7bai…9d2c",    asset:"BTC",   amount:"2.5000",   status:"signing",          statusLabel:"Signing Authorized",statusVariant:"info",   created:"10:38", creator:"Sai Suman Kanuganti", wallet:"WLT-003", state:"approved" },
  { id:"TXI-006", direction:"send",    route:"HT Polygon → 0x9e…4b1c",   asset:"MATIC", amount:"50,000",   status:"failed",           statusLabel:"Failed",           statusVariant:"danger", created:"09:55", creator:"Sai Suman Kanuganti", wallet:"WLT-004", state:"failed" },
  { id:"TXI-007", direction:"send",    route:"HT Ops Hot → 0x1a…b8c3",   asset:"ETH",   amount:"10.00",    status:"rejected",         statusLabel:"Rejected",         statusVariant:"danger", created:"09:30", creator:"Priya Sharma",  wallet:"WLT-001", state:"rejected" },
];

/* ── Transfer detail states (TRF-07..15) ─────────────────────── */
export const MOCK_TRANSFER_DETAILS: Record<string, TransferDetail> = {
  "TXI-001": {
    id:"TXI-001", direction:"send", amountMain:"50.00 ETH", amountUsd:"≈ $125,000",
    source:"HT Operations Hot (WLT-001)", destination:"0x8f2e…3a4b (external)",
    createdBy:"Sai Suman Kanuganti (USR-002) · 10:12 UTC", providerRoute:"Fireblocks",
    defaultState:"reconciled",
    evidence:[ { id:"EVD-003", label:"Broadcast receipt" }, { id:"EVD-004", label:"Reconciliation record" } ],
    timeline:[
      { title:"Draft Created",         meta:"10:12:00 UTC · Sai Suman Kanuganti (USR-002)", tone:"success" },
      { title:"Policy Evaluation Passed", meta:"10:12:02 UTC · POL-001",               tone:"success" },
      { title:"Screening Passed",      meta:"10:12:41 UTC · RiskGuard",               tone:"success" },
      { title:"Approval Completed",    meta:"10:18:00 UTC · Marcus Webb (USR-005)",   tone:"success" },
      { title:"Signing Authorized",    meta:"10:24:30 UTC · Fireblocks",              tone:"success" },
      { title:"Broadcast Submitted",   meta:"10:25:10 UTC · tx 0x8f2e…3a4b",          tone:"success" },
      { title:"Broadcast Confirmed",   meta:"10:28:44 UTC · block #18,945,102",       tone:"success" },
      { title:"Reconciled",            meta:"10:30:00 UTC · RUN-005",                 tone:"success" },
    ],
  },
  "TXI-002": {
    id:"TXI-002", direction:"receive", amountMain:"250,000.00 USDC", amountUsd:"≈ $250,000",
    source:"0x4a1b…9c2d (external)", destination:"HT Operations Hot (WLT-001)",
    createdBy:"External · observed 10:15 UTC", providerRoute:"Fireblocks",
    defaultState:"reconciled",
    finality:{ block:"#18,945,198", confirmations:"64 (finalized)", txHash:"0x9a2b…f4c1", gasUsed:"51,234 (0.0031 ETH)" },
    reconciliation:"Matched in RUN-005 at 10:30 UTC. Balance snapshot updated. No discrepancy.",
    evidence:[ { id:"EVD-004", label:"Reconciliation record" } ],
    timeline:[
      { title:"Inbound Detected",   meta:"10:15:02 UTC · Fireblocks webhook", tone:"success" },
      { title:"Screening Passed",   meta:"10:15:04 UTC · RiskGuard",          tone:"success" },
      { title:"Broadcast Confirmed",meta:"10:22:10 UTC · block #18,945,198",  tone:"success" },
      { title:"Reconciled",         meta:"10:30:00 UTC · RUN-005",            tone:"success" },
    ],
  },
  "TXI-003": {
    id:"TXI-003", direction:"send", amountMain:"100.00 ETH", amountUsd:"≈ $250,000",
    source:"HT Operations Hot (WLT-001)", destination:"HT Treasury Cold (WLT-002)",
    createdBy:"Sai Suman Kanuganti (USR-002) · 10:42 UTC", providerRoute:"Fireblocks",
    policy:"POL-001 — requires 1-of-2 approval (Security Admin or Compliance)",
    defaultState:"pending-approval",
    quorum:{ label:"Approval Quorum", needed:1, total:2, done:1, approvers:[
      { initials:"MW", name:"Marcus Webb",  role:"Security Admin · Assigned",  statusLabel:"Pending",   statusVariant:"warning" },
      { initials:"EV", name:"Elena Vasquez",role:"Compliance Officer · Alternate", statusLabel:"Available", statusVariant:"neutral" },
    ]},
    timeline:[
      { title:"Draft Created",       meta:"10:42:00 UTC · Sai Suman Kanuganti (USR-002)", tone:"success" },
      { title:"Policy Evaluation Passed", meta:"10:42:02 UTC · POL-001",            tone:"success" },
      { title:"Screening Passed",    meta:"10:42:38 UTC · RiskGuard",               tone:"success" },
      { title:"Pending Approval",    meta:"10:42:40 UTC · Marcus Webb, Elena Vasquez", detail:"Awaiting 1 of 2 approvers · SLA expires 14:42 UTC", tone:"active" },
    ],
    evidence:[ { id:"EVD-003", label:"Screening record" } ],
  },
  "TXI-004": {
    id:"TXI-004", direction:"send", amountMain:"75,000.00 USDT", amountUsd:"≈ $75,000",
    source:"HT Operations Hot (WLT-001)", destination:"0xc3d…7e2f (external)",
    createdBy:"treasury-bot (SVC-001) · 10:40 UTC", providerRoute:"Fireblocks",
    defaultState:"pending-policy",
    screening:{
      passedTitle:"Policy Evaluation — Passed",
      passedText:"POL-001: Amount within limit. Service account authorized for automated sweeps.",
      holdTitle:"Destination Screening — In Progress",
      holdText:"Chainalysis address risk assessment in progress. Started 10:40:01 UTC (5 min elapsed). Expected: <2 min.",
    },
    timeline:[
      { title:"Draft Created",        meta:"10:40:00 UTC · treasury-bot (SVC-001)", tone:"success" },
      { title:"Policy Evaluation Passed", meta:"10:40:01 UTC · POL-001",            tone:"success" },
      { title:"Screening In Progress",meta:"10:40:01 UTC · Chainalysis",            detail:"Awaiting address risk assessment", tone:"active" },
    ],
  },
  "TXI-005": {
    id:"TXI-005", direction:"send", amountMain:"2.5000 BTC", amountUsd:"≈ $162,500",
    source:"HT BTC Reserve (WLT-003)", destination:"3FE7bai…9d2c (external)",
    createdBy:"Sai Suman Kanuganti (USR-002) · 10:38 UTC", providerRoute:"BitGo",
    defaultState:"approved",
    signing:{ heading:"Signing & Broadcast Progress", note:"Note: Bitcoin transactions typically take 10-60 minutes for initial confirmation. Finality after 6 confirmations.", steps:[
      { title:"Approval Completed", meta:"10:36:00 UTC · Marcus Webb (USR-005)", tone:"success" },
      { title:"Signing Authorized", meta:"10:36:02 UTC · BitGo MPC co-sign initiated", detail:"BitGo: PENDING_SIGNATURE", tone:"active" },
      { title:"Broadcast Pending",  meta:"Awaiting BitGo signature completion (est. 1-3 min)", detail:"BitGo: SIGNING_IN_PROGRESS", tone:"pending" },
    ]},
    timeline:[
      { title:"Draft Created",        meta:"10:38:00 UTC · Sai Suman Kanuganti (USR-002)", tone:"success" },
      { title:"Policy Evaluation Passed", meta:"10:38:02 UTC · POL-001",            tone:"success" },
      { title:"Screening Passed",     meta:"10:38:40 UTC · RiskGuard",              tone:"success" },
      { title:"Approval Completed",   meta:"10:36:00 UTC · Marcus Webb (USR-005)",  tone:"success" },
      { title:"Signing Authorized",   meta:"10:36:02 UTC · BitGo",                  detail:"PENDING_SIGNATURE", tone:"active" },
    ],
  },
  "TXI-006": {
    id:"TXI-006", direction:"send", amountMain:"50,000.00 MATIC", amountUsd:"≈ $35,000",
    source:"HT Polygon Ops (WLT-004)", destination:"0x9e…4b1c (external)",
    createdBy:"Sai Suman Kanuganti (USR-002) · 09:50 UTC", providerRoute:"Fireblocks",
    defaultState:"failed",
    failure:{
      banner:"Transfer Failed: Broadcast timed out — gas estimation returned stale data. No funds were deducted.",
      failurePoint:"Broadcast — gas estimation timeout",
      error:"BROADCAST_GAS_ESTIMATION_TIMEOUT — Polygon gas oracle did not respond within 30s",
      options:[
        { title:"Retry Transfer",       description:"Re-submit with current gas parameters. Same intent, fresh broadcast attempt.", action:"Retry Transfer", variant:"primary", primary:true },
        { title:"Clone as New Intent",  description:"Create a new intent with the same parameters. Requires fresh approval.",     action:"Clone Intent",   variant:"secondary" },
        { title:"Escalate",             description:"Flag for investigation. Provider support ticket will be created.",             action:"Escalate",       variant:"secondary" },
        { title:"Cancel",               description:"Mark as terminal failure. Generate cancellation receipt.",                     action:"Cancel",         variant:"destructive" },
      ],
    },
    timeline:[
      { title:"Created",              meta:"09:50:00 UTC · Sai Suman Kanuganti (USR-002)", tone:"success" },
      { title:"Policy + Screening Passed", meta:"09:50:02 UTC",                     tone:"success" },
      { title:"Approved",             meta:"09:52:00 UTC · Marcus Webb (USR-005)",  tone:"success" },
      { title:"Signing Authorized",   meta:"09:52:05 UTC · Fireblocks",             tone:"success" },
      { title:"Broadcast Failed",     meta:"09:55:00 UTC · gas estimation timeout", detail:"Fireblocks: FAILED (error: GAS_ESTIMATION_TIMEOUT)", tone:"danger" },
    ],
  },
  "TXI-007": {
    id:"TXI-007", direction:"send", amountMain:"10.00 ETH", amountUsd:"≈ $25,000",
    source:"HT Operations Hot (WLT-001)", destination:"0x1a…b8c3 (external)",
    createdBy:"Priya Sharma (USR-003) · 09:25 UTC", providerRoute:"Fireblocks",
    defaultState:"rejected",
    rejection:{
      reason:"Destination not on approved recipient list",
      quote:"Address 0x1a…b8c3 is not in the approved external recipient list. Per policy POL-001, first-time external addresses above $10K require Compliance pre-approval. Please add to the recipient list via the address book first.",
      by:"Marcus Webb, Security Admin",
    },
    evidenceReceipt:{ evidenceId:"EVD-005", disposition:"Rejected — no funds moved" },
    timeline:[
      { title:"Draft Created",        meta:"09:25:00 UTC · Priya Sharma (USR-003)", tone:"success" },
      { title:"Policy Evaluation Passed", meta:"09:25:02 UTC · POL-001",            tone:"success" },
      { title:"Screening Passed",     meta:"09:25:40 UTC · RiskGuard",              tone:"success" },
      { title:"Rejected",             meta:"09:30:00 UTC · Marcus Webb (USR-005)",  detail:"Destination not on approved recipient list", tone:"danger" },
    ],
  },
};

export const TRANSFER_SOURCE_WALLETS: TransferWalletOption[] = [
  { id:"WLT-001", name:"HT Operations Hot",   meta:"Ethereum Mainnet · Fireblocks · $12.45M" },
  { id:"WLT-002", name:"HT Treasury Cold",    meta:"Ethereum Mainnet · Mesh (VaultCore) · $24.8M" },
  { id:"WLT-003", name:"HT BTC Reserve",      meta:"Bitcoin Mainnet · BitGo · $8.25M" },
  { id:"WLT-004", name:"HT Polygon Ops",      meta:"Polygon Mainnet · Fireblocks · $1.82M" },
  { id:"WLT-005", name:"HT Settlement",       meta:"Ethereum Mainnet · Fireblocks · $850K" },
  { id:"WLT-006", name:"HT Frozen Compliance",meta:"Ethereum Mainnet · Fireblocks · $320K", disabled:true, frozenLabel:"Cannot send — compliance freeze active" },
];

export const TRANSFER_DEST_WALLETS: TransferWalletOption[] = [
  { id:"WLT-002", name:"HT Treasury Cold", meta:"Ethereum Mainnet · Mesh (VaultCore)" },
  { id:"WLT-005", name:"HT Settlement",    meta:"Ethereum Mainnet · Fireblocks" },
];

export const TRANSFER_ASSETS: TransferAssetOption[] = [
  { symbol:"ETH",  name:"Ether",      balance:"4,125.50 ETH ($10.3M)", network:"Ethereum Mainnet", route:"Fireblocks direct" },
  { symbol:"USDC", name:"USD Coin",   balance:"1,850,000 USDC ($1.85M)", network:"Ethereum Mainnet", route:"Fireblocks direct" },
  { symbol:"USDT", name:"Tether USD", balance:"286,570 USDT ($286K)",  network:"Ethereum Mainnet", route:"Fireblocks direct" },
];

export const TRANSFER_UNSUPPORTED_NOTE =
  "Unsupported for this route: SOL (Solana not available on Fireblocks for this wallet), MATIC (different chain — use HT Polygon Ops).";

/* ── Organization Bootstrap (ORG-00..04) ─────────────────────── */

export const MOCK_BOOTSTRAP_ADMIN = {
  inviteCode: "HRBR-2026-ABCD-EFGH",
  email: "h.lindqvist@harbortrust.com",
  fullName: "Hannah Lindqvist",
  expiresAt: "2026-09-16 10:45 UTC",
};

export const MOCK_ORG_SETUP = {
  legalName: "Harbor Trust Bank, N.A.",
  jurisdiction: "United States — OCC National Bank Charter",
  tenantId: "harbor-trust-bank",
  trustDomain: "harbor-trust-prod",
  opsContact: "ops@harbortrust.com",
  securityContact: "security@harbortrust.com",
  complianceContact: "compliance@harbortrust.com",
};

export const MOCK_THEME_DEFAULT: OrgThemeConfig = {
  productName: "Harbor Trust Bank",
  primaryColor: "--ads-theme-preset-blue",
  logoFileName: null,
};

export const THEME_COLOR_PRESETS = [
  "--ads-theme-preset-blue",
  "--ads-theme-preset-teal",
  "--ads-theme-preset-violet",
  "--ads-theme-preset-green",
  "--ads-theme-preset-amber",
  "--ads-theme-preset-rose",
];

export const MOCK_ORG_ADMIN_INVITES: OrgAdminInvite[] = [
  {
    id: "INV-001",
    email: "j.chen@harbortrust.com",
    firstName: "Jordan",
    lastName: "Chen",
    role: "Org Admin",
    sentAt: "2026-09-15 08:45 UTC",
    expiresAt: "2026-09-16 08:45 UTC",
    status: "Pending",
  },
  {
    id: "INV-002",
    email: "s.patel@harbortrust.com",
    firstName: "Samir",
    lastName: "Patel",
    role: "Finance Operator",
    sentAt: "2026-09-14 10:45 UTC",
    expiresAt: "2026-09-15 10:45 UTC",
    status: "Expired",
  },
  {
    id: "INV-003",
    email: "k.mueller@harbortrust.com",
    firstName: "Kai",
    lastName: "Mueller",
    role: "Compliance Officer",
    sentAt: "2026-09-15 05:45 UTC",
    expiresAt: "2026-09-16 05:45 UTC",
    status: "Pending",
  },
];

export const ORG_INVITE_ROLES = [
  "Org Admin",
  "Finance Operator",
  "Compliance Officer",
  "Policy Approver",
];

export const MOCK_READINESS_CHECKLIST: ReadinessCheckItem[] = [
  { id: "admin", label: "Bootstrap admin activated", detail: "MFA complete · 2026-09-15 10:52 UTC", passed: true },
  { id: "org", label: "Organization configured", detail: "Harbor Trust Bank, N.A. · tenant verified", passed: true },
  { id: "branding", label: "Branding applied", detail: "Harbor Trust Bank · custom primary color", passed: true },
  { id: "quorum", label: "Admin quorum satisfied (2/2)", detail: "3 admins active · 1 pending invite", passed: true },
  { id: "recovery", label: "Backup recovery ceremony completed", detail: "Completed 2d ago · 3-of-5 quorum configured", passed: true },
];

export const MOCK_READINESS_INCOMPLETE_CHECKLIST: ReadinessCheckItem[] = [
  { id: "admin", label: "Bootstrap admin activated", detail: "MFA complete · 2026-09-15 10:52 UTC", passed: true },
  { id: "org", label: "Organization configured", detail: "Harbor Trust Bank, N.A. · tenant verified", passed: true },
  { id: "branding", label: "Branding & naming completed", detail: "Organization name and logo set", passed: true },
  { id: "quorum", label: "Admin quorum satisfied (2/2)", detail: "3 admins active · 1 pending invite", passed: true },
  { id: "recovery", label: "Backup recovery ceremony scheduled", detail: "Required before production", passed: false },
];

/* ── Identity & Access (USR-01..06) ──────────────────────────── */

export const MOCK_ROLES: RoleDefinition[] = [
  {
    id: "org-admin",
    label: "Organization Admin",
    description: "Full user, policy, and tenant administration. Cannot move funds.",
    permissions: {
      "Users": "Full",
      "Roles": "Full",
      "Groups": "Full",
      "Service Accounts": "Full",
      "Audit Log": "Read",
      "Wallets": "Read",
      "Transfers": "Read",
      "Policies": "Read",
      "Deployment": "Read",
    },
  },
  {
    id: "treasury-operator",
    label: "Treasury Operator",
    description: "Create and sign transfers within policy limits.",
    permissions: {
      "Users": "Read",
      "Roles": "None",
      "Groups": "None",
      "Service Accounts": "None",
      "Audit Log": "Read",
      "Wallets": "Read",
      "Transfers": "Create",
      "Policies": "Read",
      "Deployment": "None",
    },
  },
  {
    id: "compliance-officer",
    label: "Compliance Officer",
    description: "Screening, audit, read-only access across sensitive areas.",
    permissions: {
      "Users": "Read",
      "Roles": "None",
      "Groups": "None",
      "Service Accounts": "Read",
      "Audit Log": "Read",
      "Wallets": "Read",
      "Transfers": "Approve",
      "Policies": "Read",
      "Deployment": "Read",
    },
  },
  {
    id: "wallet-admin",
    label: "Wallet Administrator",
    description: "Create wallets, manage addresses, and configure wallet policies.",
    permissions: {
      "Users": "Read",
      "Roles": "None",
      "Groups": "None",
      "Service Accounts": "Read",
      "Audit Log": "Read",
      "Wallets": "Full",
      "Transfers": "Read",
      "Policies": "Read",
      "Deployment": "None",
    },
  },
  {
    id: "security-admin",
    label: "Security Admin",
    description: "MFA, sessions, recovery, and approval authority.",
    permissions: {
      "Users": "Full",
      "Roles": "Full",
      "Groups": "Read",
      "Service Accounts": "Full",
      "Audit Log": "Read",
      "Wallets": "Read",
      "Transfers": "Approve",
      "Policies": "Full",
      "Deployment": "Read",
    },
  },
  {
    id: "developer",
    label: "Developer",
    description: "Access sandbox and service accounts for integration work.",
    permissions: {
      "Users": "Read",
      "Roles": "None",
      "Groups": "Read",
      "Service Accounts": "Staging",
      "Audit Log": "Read",
      "Wallets": "Read",
      "Transfers": "None",
      "Policies": "Read",
      "Deployment": "Dev",
    },
  },
  {
    id: "auditor",
    label: "Auditor (Read-Only)",
    description: "Read-only access to all operational records.",
    permissions: {
      "Users": "Read",
      "Roles": "Read",
      "Groups": "Read",
      "Service Accounts": "Read",
      "Audit Log": "Read",
      "Wallets": "Read",
      "Transfers": "Read",
      "Policies": "Read",
      "Deployment": "Read",
    },
  },
  {
    id: "support-readonly",
    label: "Support Read-Only",
    description: "View user profiles and status; cannot access wallets or transfers.",
    permissions: {
      "Users": "Read",
      "Roles": "None",
      "Groups": "None",
      "Service Accounts": "None",
      "Audit Log": "Read",
      "Wallets": "None",
      "Transfers": "None",
      "Policies": "None",
      "Deployment": "None",
    },
  },
];

export const MOCK_GROUPS: UserGroup[] = [
  {
    id: "GRP-001",
    name: "Executives",
    description: "Board-level read access and high-value approvals.",
    memberIds: ["USR-001"],
    permissions: ["Transfers: Approve", "Policies: Read", "Wallets: Read"],
  },
  {
    id: "GRP-002",
    name: "Treasury Operators",
    description: "Day-to-day transfer creation and wallet operations.",
    memberIds: ["USR-002", "USR-003"],
    permissions: ["Transfers: Create", "Wallets: Read"],
  },
  {
    id: "GRP-003",
    name: "Compliance Team",
    description: "Screening, audit, and policy review.",
    memberIds: ["USR-004", "USR-009"],
    permissions: ["Transfers: Approve", "Audit Log: Read", "Policies: Read"],
  },
  {
    id: "GRP-004",
    name: "Engineering",
    description: "Sandbox, deployments, and service-account integrations.",
    memberIds: ["USR-007", "USR-008"],
    permissions: ["Service Accounts: Staging", "Deployment: Dev"],
  },
];

export const MOCK_SERVICE_ACCOUNTS: ServiceAccount[] = [
  {
    id: "SVC-001",
    name: "treasury-bot",
    owner: "Sai Suman Kanuganti (USR-002)",
    purpose: "Automated sweep and settlement intents",
    scopes: ["transfers:create", "wallets:read"],
    expiry: "2027-09-15",
    lastUsed: "2026-09-15 10:40 UTC",
    risk: "Medium",
    status: "Healthy",
  },
  {
    id: "SVC-002",
    name: "recon-connector",
    owner: "Amina Al-Rashid (USR-006)",
    purpose: "Nightly reconciliation import",
    scopes: ["wallets:read", "transfers:read"],
    expiry: "2026-12-01",
    lastUsed: "2026-09-15 06:00 UTC",
    risk: "Low",
    status: "Healthy",
  },
  {
    id: "SVC-003",
    name: "market-data-feed",
    owner: "Leo Nakamura (USR-008)",
    purpose: "Price oracle and market data ingestion",
    scopes: ["market:read"],
    expiry: "2026-10-30",
    lastUsed: "2026-09-14 22:15 UTC",
    risk: "Low",
    status: "Key Expired",
  },
  {
    id: "SVC-004",
    name: "compliance-screener",
    owner: "Elena Vasquez (USR-004)",
    purpose: "Real-time transaction screening",
    scopes: ["screening:write", "transfers:read"],
    expiry: "2027-03-20",
    lastUsed: "2026-09-15 09:55 UTC",
    risk: "High",
    status: "Auth Failing",
  },
];

export const MOCK_AUDIT_EVENTS: AuditEvent[] = [
  { id: "EVT-001", timestamp: "2026-09-15 10:45 UTC", actor: "Marcus Webb (USR-005)", action: "User suspended", target: "Rachel Kim (USR-011)", ip: "203.0.113.12", result: "Success" },
  { id: "EVT-002", timestamp: "2026-09-15 10:42 UTC", actor: "Sai Suman Kanuganti (USR-002)", action: "Transfer created", target: "TXI-003", ip: "198.51.100.45", result: "Success" },
  { id: "EVT-003", timestamp: "2026-09-15 10:40 UTC", actor: "treasury-bot (SVC-001)", action: "Transfer created", target: "TXI-004", ip: "10.0.4.18", result: "Warning" },
  { id: "EVT-004", timestamp: "2026-09-15 10:31 UTC", actor: "Priya Sharma (USR-003)", action: "Wallet creation request", target: "WLT-007", ip: "198.51.100.51", result: "Success" },
  { id: "EVT-005", timestamp: "2026-09-15 10:15 UTC", actor: "system", action: "MFA policy enforced", target: "USR-002", ip: "—", result: "Recovered" },
  { id: "EVT-006", timestamp: "2026-09-15 09:55 UTC", actor: "compliance-screener (SVC-004)", action: "Screening failed", target: "TXI-004", ip: "10.0.4.22", result: "Service Down" },
  { id: "EVT-007", timestamp: "2026-09-15 09:30 UTC", actor: "Marcus Webb (USR-005)", action: "Role permission changed", target: "treasury-operator", ip: "203.0.113.12", result: "Success" },
  { id: "EVT-008", timestamp: "2026-09-15 08:45 UTC", actor: "Hannah Lindqvist (USR-007)", action: "Invite sent", target: "j.chen@harbortrust.com", ip: "198.51.100.60", result: "Success" },
  { id: "EVT-009", timestamp: "2026-09-14 23:10 UTC", actor: "Rachel Kim (USR-011)", action: "Failed login", target: "USR-011", ip: "192.0.2.99", result: "Danger" },
  { id: "EVT-010", timestamp: "2026-09-14 18:22 UTC", actor: "Leo Nakamura (USR-008)", action: "API key rotated", target: "SVC-003", ip: "198.51.100.72", result: "Success" },
];

export const MOCK_USER_DETAILS: UserDetailExtra[] = [
  {
    userId: "USR-002",
    createdAt: "2026-03-10 14:22 UTC",
    environment: "Production",
    primaryMfa: "TOTP",
    backupMfa: "Passkey",
    recoveryCodesRemaining: 4,
    lastMfaReset: "2026-08-01 09:00 UTC",
    mfaPolicyTier: "Tier-2 — privileged operator",
    sessions: [
      { id: "SES-001", device: "Chrome 128 · macOS", location: "New York, US", ip: "198.51.100.45", startedAt: "2026-09-15 08:30 UTC", current: true },
      { id: "SES-002", device: "Safari · iOS", location: "New York, US", ip: "198.51.100.112", startedAt: "2026-09-14 17:10 UTC", current: false },
    ],
    loginHistory: [
      { id: "LGN-001", timestamp: "2026-09-15 08:30 UTC", description: "Successful login — TOTP + Passkey" },
      { id: "LGN-002", timestamp: "2026-09-14 17:10 UTC", description: "Successful login — Passkey" },
      { id: "LGN-003", timestamp: "2026-09-13 09:05 UTC", description: "Successful login — TOTP" },
      { id: "LGN-004", timestamp: "2026-09-12 22:40 UTC", description: "Failed login — invalid TOTP" },
    ],
  },
  {
    userId: "USR-005",
    createdAt: "2026-02-18 09:15 UTC",
    environment: "Production",
    primaryMfa: "Hardware Key",
    backupMfa: "TOTP",
    recoveryCodesRemaining: 6,
    lastMfaReset: "Never",
    mfaPolicyTier: "Tier-3 — security admin",
    sessions: [
      { id: "SES-010", device: "Chrome 128 · Windows", location: "London, GB", ip: "203.0.113.12", startedAt: "2026-09-15 07:45 UTC", current: true },
    ],
    loginHistory: [
      { id: "LGN-010", timestamp: "2026-09-15 07:45 UTC", description: "Successful login — Hardware Key" },
      { id: "LGN-011", timestamp: "2026-09-14 08:12 UTC", description: "Successful login — Hardware Key" },
    ],
  },
  {
    userId: "USR-011",
    createdAt: "2026-05-22 11:00 UTC",
    environment: "Production",
    primaryMfa: "TOTP",
    backupMfa: "Recovery codes",
    recoveryCodesRemaining: 1,
    lastMfaReset: "2026-09-10 16:00 UTC",
    mfaPolicyTier: "Tier-1 — standard user",
    sessions: [
      { id: "SES-020", device: "Firefox · Windows", location: "Austin, US", ip: "192.0.2.99", startedAt: "2026-08-28 15:30 UTC", current: false },
    ],
    loginHistory: [
      { id: "LGN-020", timestamp: "2026-09-14 23:10 UTC", description: "Failed login — account suspended" },
      { id: "LGN-021", timestamp: "2026-08-28 15:30 UTC", description: "Successful login — TOTP" },
    ],
  },
];

/* ── Auth Configuration (AUTH-CFG-00..04) ─────────────────────
   Source: prototype/pages/admin/auth-config.html (AUTH-CFG-00/01/02/03 —
   all four canonical screens are presented as stacked sections on ONE
   page in this prototype, not separate tabs; AUTH-CFG-00 "Login Method
   Config" and AUTH-CFG-01 "IdP Setup" are in fact the SAME "SSO
   Configuration" section in the source — see the AUTH_CONFIG_IMPLEMENTATION_REPORT
   for the tab-consolidation decision) and
   prototype/pages/admin/auth-diagnostics.html (AUTH-CFG-04). Every value
   below is transcribed verbatim; "Maya Okonkwo"/"Elena Castillo" are the
   prototype's own illustrative names and do not correspond to any real
   MOCK_USERS record (unlike "Priya Sharma", which does) — left as plain
   text, not force-linked. */

export const MOCK_SSO_CONFIG: SsoConfig = {
  provider: "Okta",
  providerOptions: ["Okta", "Azure AD", "Google Workspace", "PingFederate", "Custom SAML"],
  protocol: "OIDC",
  protocolOptions: ["OIDC", "SAML 2.0"],
  issuerUrl: "https://harbortrust.okta.com",
  clientIdRevealed: "0oa4bq71s1InVCpPH297",
  clientIdMasked: "••••••••••••••••••••••••••••••••",
  clientSecretMasked: "••••••••••••••••••••••••••••••••",
  metadataUrl: "https://harbortrust.okta.com/.well-known/openid-configuration",
  status: "Active",
};

export const SSO_TEST_RESULT = {
  title: "Connection successful",
  detail: "Issuer verified · JWKS endpoint reachable · Scopes: openid, email, profile, groups",
};

export const MOCK_MFA_POLICY: MfaMethodPolicy[] = [
  { method: "Passkey (WebAuthn)", sub: "Biometric, device PIN, or hardware key via FIDO2", status: "Required", enabled: true, enforcement: "All users" },
  { method: "TOTP Authenticator", sub: "Google Authenticator, Okta Verify, 1Password", status: "Allowed", enabled: true, enforcement: "Fallback only" },
  { method: "Hardware Security Key", sub: "YubiKey, Google Titan, FIDO2 devices", status: "Allowed", enabled: true, enforcement: "Privileged roles" },
  { method: "SMS / Voice", sub: "One-time passcode via text message or call", status: "Disabled", enabled: false, enforcement: "—" },
];

export const MOCK_SESSION_POLICY: SessionPolicy = {
  idleTimeout: "30 minutes",
  idleTimeoutOptions: ["15 minutes", "30 minutes", "1 hour"],
  absoluteTimeout: "8 hours",
  absoluteTimeoutOptions: ["4 hours", "8 hours", "12 hours"],
  maxConcurrentSessions: "3",
  maxConcurrentSessionsOptions: ["1", "2", "3", "Unlimited"],
};

export const MOCK_AUTH_CONFIG_AUDIT: AuthConfigAuditEntry[] = [
  { timestamp: "2026-01-12 16:44", actor: "Maya Okonkwo", actorInitials: "MO", description: "Required passkey for Org Admin and Finance Operator roles", tag: "MFA_POLICY", tagTone: "warning" },
  { timestamp: "2026-01-09 09:15", actor: "Maya Okonkwo", actorInitials: "MO", description: "Updated Okta SSO configuration — new metadata URL", tag: "SSO_CONFIG", tagTone: "info" },
  { timestamp: "2025-12-28 11:00", actor: "Maya Okonkwo", actorInitials: "MO", description: "Disabled SMS MFA — compliance requirement", tag: "MFA_DISABLED", tagTone: "danger" },
];

export const MOCK_AUTH_DIAGNOSTICS: Record<"sso" | "mfa" | "session", AuthDiagnosticCheck> = {
  sso: {
    title: "SSO Connection Test",
    description: "Test the SSO connection to your identity provider (Okta). Verifies metadata URL, SAML binding, and certificate validity.",
    buttonLabel: "Run SSO Test",
    resultText: "Connection successful · Response time: 240ms · Certificate valid until 2027-01-15",
  },
  mfa: {
    title: "MFA Enrollment Verification",
    description: "Verify all admins have enrolled in an MFA method. Flags any accounts without a registered passkey or TOTP device.",
    buttonLabel: "Verify MFA Enrollment",
    resultText: "3/3 admins enrolled · 0 pending enrollment · Passkey: 2 · TOTP: 1",
  },
  session: {
    title: "Active Session Diagnostics",
    description: "View active sessions across all users. Identify stale or suspicious sessions.",
    buttonLabel: "Check Sessions",
    resultText: "5 active sessions · 0 stale (>24h) · 0 from new IP · Avg session age: 2.4h",
  },
};

export const MOCK_AUTH_DIAGNOSTIC_LOGS: AuthDiagnosticLogEvent[] = [
  { timestamp: "Sep 14, 14:32", user: "Maya Okonkwo", event: "Login via SSO", ip: "192.168.1.42", details: "Okta SAML → FIDO2 MFA", result: "Success" },
  { timestamp: "Sep 14, 12:15", user: "Priya Sharma", event: "Login via SSO", ip: "10.0.0.88", details: "Okta SAML → TOTP MFA", result: "Success" },
  { timestamp: "Sep 13, 23:08", user: "Elena Castillo", event: "MFA Challenge", ip: "172.16.0.5", details: "FIDO2 challenge issued", result: "Success" },
  { timestamp: "Sep 13, 16:42", user: "Maya Okonkwo", event: "Session Revoked", ip: "—", details: "Manual logout — all devices", result: "Success" },
];

export const AUTH_DIAG_LOG_FILTER_EVENTS = ["Login Success", "Login Failure", "MFA Challenge", "Session Revoked"];
export const AUTH_DIAG_LOG_FILTER_USERS = ["Maya Okonkwo", "Priya Sharma", "Elena Castillo"];

/* ── Service Diagnostics (SVC-00..04) ─────────────────────────
   Source: prototype/pages/admin/services.html (SVC-00/01/02, one page,
   3 sub-tabs), config-viewer.html (SVC-03), support-bundle.html (SVC-04).
   These 5 internal platform services (Auth Service, Wallet Signer,
   Treasury Engine, Reconciliation Service, Notification Gateway) are the
   prototype's own distinct service catalog — deliberately NOT the same
   list as DEP_SERVICES (WalletKit/VaultCore/Alloy Mesh/Broadcast Gateway/
   PolicyKit/RiskGuard/ReconFlow), which is the frozen Deployment flow's
   own bootstrap-progress service list. The two sources define genuinely
   different service identities; no alignment between them is invented. */

export const MOCK_SVC_CATALOG: SvcCatalogEntry[] = [
  { id: "auth", name: "Auth Service", hostname: "auth.svc.alloy.internal", status: "Healthy", uptime: "98.2% uptime", latencyMs: "42 ms", latencyWarn: false, version: "v2.4.1", errors24h: 0, probeKey: "auth" },
  { id: "signer", name: "Wallet Signer", hostname: "signer.svc.alloy.internal", status: "Degraded", uptime: "91.4% uptime", latencyMs: "318 ms", latencyWarn: true, version: "v1.9.0", errors24h: 12, probeKey: "signer" },
  { id: "treasury", name: "Treasury Engine", hostname: "treasury.svc.alloy.internal", status: "Healthy", uptime: "99.1% uptime", latencyMs: "58 ms", latencyWarn: false, version: "v1.5.2", errors24h: 0, probeKey: "treasury" },
  { id: "recon", name: "Reconciliation Service", hostname: "recon.svc.alloy.internal", status: "Healthy", uptime: "98.7% uptime", latencyMs: "66 ms", latencyWarn: false, version: "v1.3.8", errors24h: 0, probeKey: "recon" },
  { id: "notify", name: "Notification Gateway", hostname: "notify.svc.alloy.internal", status: "Healthy", uptime: "99.5% uptime", latencyMs: "31 ms", latencyWarn: false, version: "v1.2.0", errors24h: 0, probeKey: "notify" },
];

export const MOCK_SVC_CONNECTIVITY: SvcConnectivityCheck[] = [
  { pair: "Auth → User Store", result: "Pass", latency: "38 ms", certificate: "Valid · 2027-03-10", lastCheck: "2m ago" },
  { pair: "Auth → SSO IdP", result: "Pass", latency: "94 ms", certificate: "Valid · 2027-01-15", lastCheck: "2m ago" },
  { pair: "Treasury → Wallet Signer", result: "Partial", latency: "318 ms", certificate: "Expires in 6d", lastCheck: "2m ago" },
  { pair: "Treasury → Custody API", result: "Pass", latency: "112 ms", certificate: "Valid · 2027-06-01", lastCheck: "4m ago" },
  { pair: "Recon → Custody API", result: "Pass", latency: "120 ms", certificate: "Valid · 2027-06-01", lastCheck: "4m ago" },
  { pair: "Recon → Ledger DB", result: "Fail", latency: "Dropped", certificate: "Failed handshake", lastCheck: "6m ago" },
  { pair: "Notify → Email Relay", result: "Pass", latency: "205 ms", certificate: "Valid · 2027-11-22", lastCheck: "3m ago" },
];

/* The canonical prototype's own runProbe(which) JS always displays this
   SAME source/target/protocol/start regardless of which service triggered
   it, varying only the completion RTT/message — a simplification in the
   source itself, reproduced faithfully rather than invented or "improved"
   into a fully per-service-accurate simulation. */
export const SVC_PROBE_DEFAULTS: SvcProbeDefaults = {
  label: "Treasury → Wallet Signer",
  source: "treasury.svc.alloy.internal:8443",
  target: "signer.svc.alloy.internal:8443",
  protocol: "mTLS / HTTP/2",
  start: "2026-09-14T14:32:05Z",
  probeId: "probe #4521",
  intervalLabel: "scheduled every 2m",
};

export const SVC_CONFIG_TREE: SvcConfigNode[] = [
  { key: "network", value: '": { "chainId": 11155111, "networkName": "sepolia" }', source: "override · production-policy@9a2f", sourceTone: "success", level: 1 },
  { key: "rpc", value: '": { "eth": "https://rpc.sepolia.8ball.network", "timeoutMs": 5000, "retries": 3 }', source: "inherited · org-global@scope", sourceTone: "info", level: 1 },
  { key: "signing", value: '": { "scheme": "ed25519", "hsm": "/dev/tpm0", "debugConsole": false }', source: "override · platform@v2.4", sourceTone: "success", level: 1 },
  { key: "quorum", value: '": { "threshold": 3, "memberCount": 5, "mode": "m-of-n" }', source: "default", sourceTone: "neutral", level: 2 },
  { key: "treasury", value: '": { "autopay": { "enabled": false }, "routePolicy": "conservative" }', source: "inherited · treasury-module@0a1c", sourceTone: "info", level: 1 },
  { key: "sweepThresholdUsd", value: '": 100000', source: "default", sourceTone: "neutral", level: 2 },
  { key: "observability", value: '": { "traces": "otlp", "metricsInterval": "30s", "logLevel": "info" }', source: "inherited · platform@b77e", sourceTone: "info", level: 1 },
  { key: "sensitiveRedaction", value: '": true', source: "override · compliance-required", sourceTone: "success", level: 2 },
];

export const SVC_CONFIG_META = { environment: "testnet", service: "wallet-signer", asOf: "2026-09-14T14:30:00Z", keyCount: 37 };

export const SVC_BUNDLE_INCLUDES: SvcSupportBundleInclude[] = [
  { label: "Service health snapshot", defaultChecked: true },
  { label: "Effective config (redacted secrets)", defaultChecked: true },
  { label: "Last 500 auth & audit events", defaultChecked: true },
  { label: "Connectivity matrix (last 20 checks)", defaultChecked: true },
  { label: "Recent error stack traces", defaultChecked: false },
  { label: "Network hops & TLS handshake traces", defaultChecked: false },
];

export const SVC_RECENT_BUNDLES: SvcSupportBundleRecord[] = [
  { filename: "bundle-harbortrust-2026-09-12-1042.zip", status: "Ready to download", sizeLabel: "14.8 MB" },
  { filename: "bundle-harbortrust-2026-09-10-0831.zip", status: "Downloaded", sizeLabel: "12.1 MB" },
  { filename: "bundle-harbortrust-2026-09-08-1630.zip", status: "Expired", sizeLabel: "" },
];

/* ── Recovery / Key Continuity (RCV-00..05) ───────────────────
   Source: prototype/pages/recovery/{index,backup,request,approval,
   execution,audit}.html (no current Figma frame located for any RCV
   screen). Values are transcribed verbatim EXCEPT: (1) the "Affected
   Wallet" options on RCV-02, which the canonical source lists as
   WLT-ILL-001..004 — the illustrative wallet ids explicitly retired
   elsewhere in this engagement (see treasury-data.ts's own header
   comment); substituted with the real MOCK_WALLETS universe (WLT-001..
   008) instead of resurrecting retired ids. (2) RCV-00's canonical
   "Active Ceremony: REC-2024-001 →" action-bar link, which contradicts
   the SAME page's own "Active Requests: 0 / No pending requests" KPI
   card and points to a non-required prototype page (hsm-auth.html, not
   one of RCV-00..05) — omitted rather than reproducing a self-
   contradictory, out-of-scope link; documented in the batch report, not
   silently dropped. RCV-02/03 and RCV-04/05 each preserve their own
   canonical example request ids (REC-2024-001 and REC-2025-113/114/115
   respectively) exactly as the source keeps them separate — no artificial
   single-thread ID was invented across all 6 screens. */

export const RECOVERY_POSTURE = {
  keyShards: "5",
  shardScheme: "3-of-5 scheme",
  custodianCount: "3",
  custodiansSub: "All online",
  activeRequests: "0",
  activeRequestsSub: "No pending requests",
  lastTested: "28d ago",
  lastTestedSub: "Test due in 14 days",
  status: "Healthy" as const,
  scheme: "Shamir's Secret Sharing (3-of-5)",
  keyAlgorithm: "Ed25519 + secp256k1",
  shardStorage: "HSM (3) + Human (2)",
  nextTest: "2026-02-12 (14 days)",
  complianceRequirement: "SOC2 · ISO 27001",
};

export const RECOVERY_HSM_SHARDS: RecoveryShard[] = [
  { label: "Shard 1", holder: "Alloy Vault" },
  { label: "Shard 2", holder: "Fireblocks" },
  { label: "Shard 3", holder: "Anchorage" },
];

export const RECOVERY_HUMAN_SHARDS: RecoveryShard[] = [
  { label: "Shard 4", holder: "M. Okonkwo" },
  { label: "Shard 5", holder: "D. Park" },
];

export const RECOVERY_CUSTODIANS: RecoveryCustodian[] = [
  { name: "Alloy Vault (HSM-01)", role: "Primary", type: "HSM", status: "Online", statusTone: "success", lastVerified: "2h ago" },
  { name: "Fireblocks MPC", role: "Secondary", type: "MPC", status: "Online", statusTone: "success", lastVerified: "5m ago" },
  { name: "Anchorage Vault", role: "Secondary", type: "HSM", status: "Online", statusTone: "success", lastVerified: "1h ago" },
  { name: "Maya Okonkwo — Org Admin", role: "Key Holder", type: "Human", status: "Confirmed", statusTone: "success", lastVerified: "28d ago" },
  { name: "Daniel Park — Finance Operator", role: "Key Holder", type: "Human", status: "Confirmed", statusTone: "success", lastVerified: "28d ago" },
];

export const RECOVERY_HISTORY: RecoveryHistoryRow[] = [
  { date: "2025-12-15", type: "Test Only", participants: "Maya O., Daniel P., Alloy Vault", duration: "18 min", result: "Passed", ref: "REC-2025-004" },
  { date: "2025-09-14", type: "Test Only", participants: "Maya O., Daniel P., Anchorage Vault", duration: "22 min", result: "Passed", ref: "REC-2025-003" },
  { date: "2025-06-10", type: "Test Only", participants: "Maya O., Fireblocks MPC", duration: "31 min", result: "Partial", ref: "REC-2025-002" },
  { date: "2025-03-05", type: "Shard Replacement", participants: "All Custodians", duration: "47 min", result: "Passed", ref: "REC-2025-001" },
];

export const RECOVERY_TEST_TOAST = "Recovery test flow — planned in a future release (P6)";

export const BACKUP_CHECKLIST: BackupChecklistStep[] = [
  { label: "Create backup session", detail: "Session BKP-2026-044 created · 3-of-5 scheme · under-approval", done: true },
  { label: "Confirm 5 custodians online", detail: "All custodians verified via passkey · 3 present in room, 2 remote", done: true },
  { label: "Generate key shards", detail: "Shards will be generated in-hardware; no plaintext key material leaves the HSM", done: false },
  { label: "Print & seal envelopes", detail: "Each custodian verifies envelope serial numbers before signing", done: false },
  { label: "Distribute & confirm receipt", detail: "Custodians acknowledge custody via HSM-authenticated receipt", done: false },
];

export const BACKUP_PREVIOUS = "BKP-2025-038 · completed 2025-08-17 · 3-of-5 · custodians: Okonkwo, Chen, Patel, Li, Sharma";

export const BACKUP_COMPLETE = {
  id: "BKP-2026-044",
  hash: "SHA256 9f2a…c0e1",
  timestamp: "2026-09-14T14:35:00Z",
};

export const RECOVERY_AFFECTED_WALLET_OPTIONS = ["All Wallets (default for test)", ...MOCK_WALLETS.map((w) => `${w.id} — ${w.name}`)];

export const RECOVERY_REQUEST_DEFAULTS = {
  recoveryType: "Test Only",
  reason: "Scheduled quarterly key recovery test per SOC2 compliance requirements (Q1 2026).",
  affectedWallet: "All Wallets (default for test)",
  urgency: "normal" as const,
  messagePreview: (requesterName: string, type: string, reason: string) =>
    `"You have been notified of a key recovery request (${type}) by ${requesterName}. Reason: ${reason} Your approval is required before recovery can proceed. Please review in Alloy."`,
};

export const RECOVERY_REQUEST_CUSTODIANS = [
  { name: "Alloy Vault (HSM-01)", sub: "Primary · Auto-notified", required: true },
  { name: "Fireblocks MPC", sub: "Secondary HSM · Online", required: false },
  { name: "Anchorage Vault", sub: "Secondary HSM · Online", required: false },
  { name: "Maya Okonkwo", sub: "Org Admin · Key Holder", required: false },
  { name: "Priya Sharma", sub: "Policy Approver · Key Holder", required: false },
];

export const RECOVERY_REQUEST_2024_001: RecoveryRequestFixture = {
  id: "REC-2024-001",
  type: "Test Only",
  reason: "Scheduled quarterly key recovery test per SOC2 compliance requirements",
  affectedWallet: "All Wallets",
  urgency: "Normal",
  requester: "Daniel Park",
  requesterRole: "Finance Operator",
  submitted: "2026-01-15 11:45 UTC",
};

export const RECOVERY_APPROVALS_2024_001: CustodianApproval[] = [
  { initials: "AV", color: "var(--ads-viz-charcoal)", name: "Alloy Vault (HSM-01)", role: "Primary · HSM", status: "Approved automatically", isMine: false },
  { initials: "FM", color: "var(--ads-viz-charcoal)", name: "Fireblocks MPC", role: "Secondary · MPC", status: "Approved automatically", isMine: false },
  { initials: "MO", color: "var(--ads-primary)", name: "Maya Okonkwo", role: "Org Admin · Key Holder", status: "Approved", isMine: false },
  { initials: "PS", color: "var(--ads-pending)", name: "Priya Sharma", role: "Policy Approver · Key Holder", status: "Pending", isMine: true },
  { initials: "DP", color: "var(--ads-asset-ada)", name: "Daniel Park", role: "Finance Operator · Requester", status: "Abstained", isMine: false },
];

export const RECOVERY_APPROVAL_QUORUM_NOTE = "2 of 3 required approvals received. Your approval will initiate the recovery process.";

export const RECOVERY_EXECUTIONS: Record<"in-progress" | "complete" | "failed", RecoveryExecutionState> = {
  "in-progress": {
    id: "REC-2026-114",
    status: "In progress",
    keyFingerprint: "K-90f2…",
    method: "HSM shard re-assembly",
    quorum: "3-of-5",
    progressPct: 58,
    steps: [
      { label: "Approved", state: "done" },
      { label: "Quorum gathered", state: "done" },
      { label: "Shard exchange", state: "active" },
      { label: "Key reconstitution", state: "pending" },
      { label: "Verification", state: "pending" },
    ],
    log: [
      { time: "14:32:08", text: "Shard 1/5 received — fingerprint ok" },
      { time: "14:32:14", text: "Shard 2/5 received — fingerprint ok" },
      { time: "14:32:21", text: "Shard 3/5 received — fingerprint ok" },
      { time: "14:32:24", text: "Exchange routing verified (HSM-attested)" },
    ],
  },
  complete: {
    id: "REC-2026-113",
    status: "Complete",
    keyFingerprint: "K-88e1…",
    method: "HSM shard re-assembly",
    quorum: "3-of-5",
    duration: "12m 38s",
    progressPct: 100,
    steps: [
      { label: "Approved", state: "done" },
      { label: "Quorum gathered", state: "done" },
      { label: "Shard exchange", state: "done" },
      { label: "Reconstituted", state: "done" },
      { label: "Verified", state: "done" },
    ],
    log: [],
  },
  failed: {
    id: "REC-2026-115",
    status: "Failed",
    keyFingerprint: "K-77b0…",
    method: "HSM shard re-assembly",
    quorum: "3-of-5",
    progressPct: 66,
    steps: [
      { label: "Approved", state: "done" },
      { label: "Quorum gathered", state: "done" },
      { label: "Shard exchange", state: "failed" },
      { label: "Aborted", state: "pending" },
    ],
    log: [
      { time: "14:42:10", text: "Shard 1/5 received — fingerprint ok" },
      { time: "14:42:16", text: "Shard 2/5 received — fingerprint ok" },
      { time: "14:42:23", text: "Shard 3/5 received — fingerprint mismatch", danger: true },
      { time: "14:42:23", text: "Exchange aborted by policy enforcement #P-118" },
    ],
    failureBanner: "Execution failed — shard exchange aborted at 3/5. One shard failed fingerprint verification. Recovery request reopened for investigation.",
  },
};

export const RECOVERY_AUDIT_PACKET: RecoveryAuditPacket = {
  id: "REC-2026-113",
  createdAt: "2026-09-14T10:12:33Z",
  requestedBy: "Maya Okonkwo · Org Admin",
  justification: "Lost HSM slot 3 after hardware refresh",
  approval: "Approved 2-of-5 · by Okonkwo, Chen",
  executionWindow: "2026-09-14T14:20:00Z → 14:32:38Z",
  quorum: "3-of-5 · custodians fully authed (HSM-attested)",
  keyFingerprint: "K-88e1… fd42 (verified)",
  result: "Complete",
  merkleRoot: "0x7f3e…a91c",
  blockRefs: "[chain: error-beam, slot: 88421, tx: 0x2b1c…c4e8] · [chain: error-beam, slot: 88429, tx: 0x9d30…f7d2]",
  signatures: [
    { name: "Maya Okonkwo", sig: "0x…a2f1", method: "passkey", timestamp: "14:31:02Z" },
    { name: "James Chen", sig: "0x…77b8", method: "passkey", timestamp: "14:31:09Z" },
    { name: "Amina Diallo", sig: "0x…01e4", method: "TOTP", timestamp: "14:31:15Z" },
  ],
};

/* ── Developer (DEV-00..02) ──────────────────────────────────
   Source: prototype/pages/developer/{index,api-keys,webhooks}.html
   (no current Figma frame located). Values transcribed verbatim
   EXCEPT: (1) the Recent API Activity log's `/wallets/WLT-ILL-001`
   row and the webhook payload sample's `"from_wallet": "WLT-ILL-002"`
   field — both illustrative retired wallet ids, replaced with real
   MOCK_WALLETS ids (WLT-001, WLT-002) per this engagement's wallet-id
   hygiene rule (never resurrect WLT-ILL-*). (2) DEV-01's "Generate
   Key →" reveal — the canonical source displays a fully JWT-shaped
   fake secret (`ak_test_eyJhbGci...7f2a`) that could be mistaken for
   a real credential if copied. Per this task's explicit "never expose
   fabricated API secrets" instruction, the revealed value is instead
   generated client-side in an unmistakably local/mock format
   (`ak_local_mock_<random>`) with explicit on-screen copy stating no
   backend key infrastructure exists — see `features/developer/api-
   keys.tsx`. (3) The canonical page-level "Testnet" env badge is
   omitted — this app's frozen global shell topbar already carries its
   own environment/tenant badge ("Harbor Trust Bank · Production"); a
   second, contradictory per-page env badge is not reproduced. (4) The
   canonical "SDK Docs ↗" / "API Reference ↗" quicknav cards link to
   external docs domains (docs.alloy.finance, api.alloy.finance/docs)
   that are not part of this app; reproduced as inert cards that show
   an honest "external documentation — not available in this build"
   toast rather than navigating to an unverified external URL. */

export const DEV_ENVIRONMENT = {
  environment: "Testnet",
  baseUrl: "https://api.alloy.finance/v1/testnet",
  apiVersion: "v1.2.0",
  rateLimit: "1,000 req/min",
  orgId: "org_harbortrust_01",
};

export const DEV_API_ACTIVITY: DevApiActivityRow[] = [
  { method: "GET", endpoint: "/transfers", status: 200, ok: true, latencyMs: 42, timestamp: "Jan 15 14:30" },
  { method: "POST", endpoint: "/transfers", status: 201, ok: true, latencyMs: 318, timestamp: "Jan 15 14:28" },
  { method: "GET", endpoint: "/wallets", status: 200, ok: true, latencyMs: 28, timestamp: "Jan 15 14:25" },
  { method: "GET", endpoint: "/transfers/TXF-2024-0891", status: 200, ok: true, latencyMs: 35, timestamp: "Jan 15 14:20" },
  { method: "POST", endpoint: "/transfers/approve", status: 200, ok: true, latencyMs: 521, timestamp: "Jan 15 13:30" },
  { method: "GET", endpoint: "/evidence/TXF-2024-0891", status: 200, ok: true, latencyMs: 88, timestamp: "Jan 15 13:00" },
  { method: "POST", endpoint: "/webhooks/test", status: 200, ok: true, latencyMs: 142, timestamp: "Jan 15 11:15" },
  { method: "GET", endpoint: "/reconciliation/status", status: 200, ok: true, latencyMs: 54, timestamp: "Jan 15 10:40" },
  { method: "POST", endpoint: "/transfers", status: 422, ok: false, latencyMs: 95, timestamp: "Jan 15 09:30" },
  { method: "GET", endpoint: "/wallets/WLT-001", status: 200, ok: true, latencyMs: 31, timestamp: "Jan 15 09:00" },
];

export const DEV_API_HEALTH = [
  { name: "Transfer API", status: "Online" as const },
  { name: "Wallet API", status: "Online" as const },
  { name: "Evidence API", status: "Online" as const },
  { name: "Webhook Delivery", status: "Online" as const },
];

export const DEV_QUICK_START_CODE = `# Install SDK
npm install @alloy/sdk

# Initialize client
import { AlloyClient } from '@alloy/sdk';

const client = new AlloyClient({
  apiKey: 'ak_test_...',
  env: 'testnet'
});

# List pending approvals
const transfers = await
  client.transfers.list({
    status: 'pending-approval'
  });`;

export const MOCK_API_KEYS: ApiKey[] = [
  { id: "key-1", name: "Production Key", keyPreview: "ak_prod_...3f8a", scopes: "read:transfers, write:transfers, read:wallets", created: "Dec 1, 2023", lastUsed: "Just now", status: "Active" },
  { id: "key-2", name: "Reporting Key", keyPreview: "ak_prod_...9c2e", scopes: "read:*", created: "Nov 15, 2023", lastUsed: "2h ago", status: "Active" },
  { id: "key-3", name: "Webhook Signing", keyPreview: "wh_prod_...4d7b", scopes: "webhooks", created: "Dec 1, 2023", lastUsed: "5m ago", status: "Active" },
  { id: "key-4", name: "Old Dev Key", keyPreview: "ak_test_...1a4f", scopes: "read:*", created: "Oct 1, 2023", lastUsed: "45d ago", status: "Revoked" },
];

export const DEV_KEY_SCOPES = ["read:transfers", "write:transfers", "read:wallets", "write:wallets", "read:evidence", "admin:*"];
export const DEV_KEY_EXPIRY_OPTIONS = ["30d", "90d", "1y", "No expiry"];

export const MOCK_WEBHOOK_ENDPOINTS: WebhookEndpoint[] = [
  { id: "wh-1", url: "api.harbortrust.com/alloy/webhooks", events: "transfer.*", status: "Active", lastDeliveryOk: true, lastDeliveryCode: 200, lastDeliveryWhen: "2m ago" },
  { id: "wh-2", url: "siem.harbortrust.internal/events", events: "*.*", status: "Active", lastDeliveryOk: true, lastDeliveryCode: 200, lastDeliveryWhen: "5m ago" },
  { id: "wh-3", url: "old-endpoint.harbortrust.com/hook", events: "transfer.created", status: "Disabled", lastDeliveryOk: false, lastDeliveryCode: 503, lastDeliveryWhen: "30d ago" },
];

export const DEV_WEBHOOK_EVENTS = [
  "transfer.created",
  "transfer.approved",
  "transfer.status_changed",
  "transfer.rejected",
  "wallet.balance_changed",
  "approval.required",
  "evidence.recorded",
  "reconciliation.flagged",
];

export const DEV_WEBHOOK_DEFAULT_EVENTS = ["transfer.created", "transfer.approved", "transfer.status_changed"];

export const MOCK_WEBHOOK_DELIVERIES: WebhookDelivery[] = [
  { event: "transfer.status_changed", endpoint: "harbortrust.com/...", statusCode: 200, ok: true, latencyMs: 42, timestamp: "Jan 15 14:30" },
  { event: "transfer.approved", endpoint: "harbortrust.com/...", statusCode: 200, ok: true, latencyMs: 38, timestamp: "Jan 15 13:30" },
  { event: "transfer.created", endpoint: "harbortrust.com/...", statusCode: 200, ok: true, latencyMs: 55, timestamp: "Jan 15 09:00" },
  { event: "transfer.created", endpoint: "old-endpoint...", statusCode: 503, ok: false, latencyMs: null, timestamp: "Jan 14 09:00", retryable: true },
];

export const DEV_WEBHOOK_PAYLOAD_SAMPLE = `{
  "event": "transfer.status_changed",
  "id": "evt_01HKXYZ789ABC",
  "created_at": "2024-01-15T14:30:00Z",
  "org_id": "org_harbortrust_01",
  "data": {
    "transfer_id": "TXF-2024-0891",
    "previous_status": "pending-approval",
    "new_status": "approved",
    "amount": 250000.00,
    "asset": "USDC",
    "network": "ethereum",
    "from_wallet": "WLT-002",
    "to_address": "0x742d35Cc...4e3b",
    "approved_by": [
      "priya.sharma@harbortrust.com"
    ]
  }
}`;

export const DEV_WEBHOOK_DELIVERY_PAYLOAD = `{
  "event": "transfer.status_changed",
  "id": "evt_01HKXYZ789ABC",
  "created_at": "2024-01-15T14:30:00Z",
  "data": { "transfer_id": "TXF-2024-0891", "new_status": "approved" }
}`;

/* ── Settings (SET-00, SET-02) ────────────────────────────────
   Source: prototype/pages/admin/settings.html (no current Figma
   frame located). Per the blueprint, SET-00 covers ONLY this page's
   "Organization" tab and SET-02 covers ONLY its "Notifications" tab
   — the page's other three tabs (Security, Billing, Integrations)
   are not sourced by any required screen ID and are NOT implemented
   here, per this task's "do not invent settings categories"
   instruction. DD-007 (tab/route structure) is resolved identically
   to DD-004/005/006: query-param tabs on one route
   (`/settings?tab=organization|notifications`).

   The blueprint's suggested data dependency for SET-02
   ("MOCK_NOTIFICATIONS + user preference fixture") was investigated
   rather than assumed: the existing `MOCK_NOTIFICATIONS` fixture is
   the topbar bell's notification FEED (unrelated shape — received
   notifications, not save-able preference toggles). The canonical
   settings.html page's own Notifications tab is a fixed set of 6
   preference checkboxes with their own labels/descriptions/defaults,
   wired to nothing else — a new, dedicated fixture
   (`SETTINGS_NOTIFICATION_PREFS`) was created instead of forcing an
   unrelated reuse.

   The Organization tab's own "Organization ID" (`ORG-HTB-001`) and
   the Developer module's own "Org ID" (`org_harbortrust_01`, Batch 6)
   are two distinct canonical values from two distinct prototype
   files, representing different id namespaces (human-readable org
   registry id vs. API-facing org slug) — kept as each file's own
   canonical value, not force-unified. */

export const SETTINGS_ORG_PROFILE = {
  orgName: "Harbor Trust Bank",
  legalName: DEMO_CUSTOMER.orgName as string,
  orgId: "ORG-HTB-001",
  primaryDomain: "harbortrust.com",
  timezone: "America/Chicago",
  country: "United States",
};

export const SETTINGS_TIMEZONE_OPTIONS = ["UTC", "America/New_York", "America/Chicago", "America/Los_Angeles"];
export const SETTINGS_COUNTRY_OPTIONS = ["United States", "United Kingdom", "Singapore"];

export const SETTINGS_NOTIFICATION_PREFS: NotificationPrefGroup[] = [
  {
    group: "Transfer Events",
    items: [
      { key: "transfer-approval", label: "Transfer requires approval", desc: "Notify approvers when a new transfer enters pending-approval state", defaultChecked: true },
      { key: "transfer-failed", label: "Transfer failed or rejected", desc: "Alert operators when transfers enter failed or rejected state", defaultChecked: true },
      { key: "transfer-confirmed", label: "Transfer confirmed on-chain", desc: "Confirmation email when transfer is broadcast-confirmed", defaultChecked: false },
    ],
  },
  {
    group: "System Alerts",
    items: [
      { key: "custody-latency", label: "Custody provider latency alert", desc: "Alert when Fireblocks or Anchorage latency exceeds threshold", defaultChecked: true },
      { key: "recon-unmatched", label: "Reconciliation unmatched items", desc: "Daily digest of unmatched transactions from ReconFlow", defaultChecked: true },
      { key: "policy-blocked", label: "Policy triggered or blocked", desc: "Notify when a governance policy blocks a transfer", defaultChecked: false },
    ],
  },
];
